import React from 'react';

import Button, { LoadingButton } from '@atlaskit/button';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import LockIcon from '@atlaskit/icon/glyph/lock';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { FormattedMessage } from '@atlassian/jira-intl';
import type { OgIntegration } from '../../../../../common/types';
import { useChatToolsActions, useChatToolsStore } from '../../../../../services/store/chat-tools';
import { messages } from './messages';

export const Action = ({ item }: { item: OgIntegration }) => {
	const { givePermissionToUser, duplicateIntegration } = useChatToolsActions();
	const [{ loadingIntegrationId }] = useChatToolsStore();

	if (item.status === 'COPIED') {
		return (
			<>
				<CheckCircleIcon
					size="small"
					label=""
					primaryColor={token('color.icon.success', '#22A06B')}
				/>
				<FormattedMessage {...messages.copyIntegrationsTableCopiedTextNonFinal} />
			</>
		);
	}

	if (item.status === 'UNCONNECTED') {
		return (
			<Tooltip
				position="top"
				content={
					<FormattedMessage
						{...(item.chatSpaceType === 'slack'
							? messages.slackUnconnectedTooltipNonFinal
							: messages.mstUnconnectedTooltipNonFinal)}
					/>
				}
			>
				<Button
					appearance="subtle-link"
					isDisabled
					iconBefore={
						<LockIcon size="small" label="" primaryColor={token('color.icon.subtle', '#626F86')} />
					}
				>
					<FormattedMessage {...messages.copyIntegrationsTableUnconnectedTextNonFinal} />
				</Button>
			</Tooltip>
		);
	}

	if (item.status === 'AWAITED') {
		return (
			<Tooltip
				position="top"
				content={
					<FormattedMessage
						{...(item.chatSpaceType === 'slack'
							? messages.slackGivePermissionTooltipNonFinal
							: messages.mstGivePermissionTooltipNonFinal)}
					/>
				}
			>
				<LoadingButton
					isLoading={loadingIntegrationId === item.id}
					onClick={() => givePermissionToUser(item)}
					appearance="subtle-link"
					iconBefore={
						<ErrorIcon size="small" label="" primaryColor={token('color.icon.subtle', '#626F86')} />
					}
				>
					<FormattedMessage {...messages.givePermissionTextNonFinal} />
				</LoadingButton>
			</Tooltip>
		);
	}

	if (item.status === 'GRANTED') {
		return (
			<LoadingButton
				onClick={() => duplicateIntegration(item)}
				isLoading={loadingIntegrationId === item.id}
				iconBefore={<CopyIcon label="Copy" size="small" />}
			>
				<FormattedMessage {...messages.copyToJiraNonFinal} />
			</LoadingButton>
		);
	}

	// Only remaining case is FAILED and we users should be retry to copy again in this case
	return (
		<LoadingButton
			onClick={() => duplicateIntegration(item)}
			isLoading={loadingIntegrationId === item.id}
			iconBefore={<CopyIcon label="Copy" size="small" />}
		>
			<FormattedMessage {...messages.copyToJiraNonFinal} />
		</LoadingButton>
	);
};
